import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  type SelectOptionType,
  type DocumentChoice,
  type IVIntent,
  IV_DOCUMENT_IMAGE_TYPES,
} from '@guestapp/sdk';
import {FORM_NAMES} from 'utils/guestForm';
import {useKioskoStore} from 'context/kiosko/useKioskoStore';
import {useIVStore} from 'store/identityVerification';
import {PATHS} from 'Routes';
import {useUpdateIvIntent} from 'hooks/iv/useUpdateIvIntent';
import {getIVIntentRemoteSession} from 'hooks/iv/useIVRemoteSync';

type FormTypes = {
  [FORM_NAMES.nationality]: SelectOptionType;
  [FORM_NAMES.document_type]: SelectOptionType<DocumentChoice> | null;
};

type Props = {
  onSuccess?: (data: IVIntent) => void;
};
export const useDocumentSelection = ({onSuccess}: Props = {}) => {
  const navigate = useNavigate();
  const {setDocumentType, setNationality} = useIVStore();
  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const goToUploading = useCallback(() => {
    const navigateTo = isKiosko
      ? `../${PATHS.verification?.front}/scan`
      : `../${PATHS.verification.uploadingType}/${IV_DOCUMENT_IMAGE_TYPES.front}`;

    navigate(navigateTo);
  }, [navigate, isKiosko]);

  const {sendUpdateIvIntentRequest, isLoading} = useUpdateIvIntent({
    onSuccess,
    postHandler: goToUploading,
  });

  const confirm = useCallback(
    (data: FormTypes) => {
      const docType = data?.[FORM_NAMES.document_type]?.data;
      if (docType) {
        setDocumentType(docType);
      }
      const nationality = data[FORM_NAMES.nationality]?.value;
      if (nationality) {
        setNationality(nationality);
      }

      const intentId = getIVIntentRemoteSession();
      if (intentId && docType && nationality) {
        sendUpdateIvIntentRequest({
          id: intentId,
          nationality,
          type_doc: docType,
        });
      } else {
        goToUploading();
      }
    },
    [goToUploading, sendUpdateIvIntentRequest, setDocumentType, setNationality],
  );

  return {
    isLoading,
    confirm,
  };
};
